import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Clock, MapPin, ArrowUpRight, Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", company: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We'll reply within one business day.");
      setForm(empty);
    } catch {
      toast.error("Something went wrong. Please email us directly at kkumar4601@gmail.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-36">
      <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-sky-400 md:text-sm">
            04 — Start the Conversation
          </p>
          <h2 className="font-display text-4xl font-bold tracking-tight md:text-6xl">
            Tell us what's slowing you down.
          </h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400 md:text-lg">
            Share a few details and we'll come back with a tailored plan and pricing —
            usually within one business day.
          </p>

          <div className="mt-12 space-y-6">
            <a
              href="tel:+917989534340"
              data-testid="contact-phone-link"
              className="group flex items-center gap-4 text-lg font-semibold text-white transition-colors duration-300 hover:text-sky-400"
            >
              <span className="flex h-11 w-11 items-center justify-center border border-white/15 transition-colors duration-300 group-hover:border-sky-400">
                <Phone className="h-4 w-4 text-sky-400" />
              </span>
              +91 79895 34340
            </a>
            <a
              href="mailto:kkumar4601@gmail.com"
              data-testid="contact-email-link"
              className="group flex items-center gap-4 text-lg font-semibold text-white transition-colors duration-300 hover:text-sky-400"
            >
              <span className="flex h-11 w-11 items-center justify-center border border-white/15 transition-colors duration-300 group-hover:border-sky-400">
                <Mail className="h-4 w-4 text-sky-400" />
              </span>
              kkumar4601@gmail.com
            </a>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/15">
                <MapPin className="h-4 w-4 text-sky-400" />
              </span>
              <span data-testid="contact-location" className="text-lg font-semibold text-white">
                Kurnool, Andhra Pradesh, India
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-slate-400">
              <span className="flex h-11 w-11 items-center justify-center border border-white/15">
                <Clock className="h-4 w-4 text-sky-400" />
              </span>
              Response within one business day — usually faster.
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-white/10 bg-[#0D1424] p-8 md:p-10"
          >
            <div className="grid gap-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                    Name *
                  </label>
                  <Input
                    id="contact-name"
                    data-testid="contact-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Cooper"
                    className="h-12 rounded-none border-white/15 bg-[#0A0F1C] text-white placeholder:text-slate-600 focus-visible:ring-sky-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                    Work Email *
                  </label>
                  <Input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@company.com"
                    className="h-12 rounded-none border-white/15 bg-[#0A0F1C] text-white placeholder:text-slate-600 focus-visible:ring-sky-400"
                  />
                </div>
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-company" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                    Company
                  </label>
                  <Input
                    id="contact-company"
                    data-testid="contact-company-input"
                    value={form.company}
                    onChange={set("company")}
                    placeholder="Acme Inc."
                    className="h-12 rounded-none border-white/15 bg-[#0A0F1C] text-white placeholder:text-slate-600 focus-visible:ring-sky-400"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                    Service Needed
                  </label>
                  <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                    <SelectTrigger
                      data-testid="contact-service-select"
                      className="h-12 rounded-none border-white/15 bg-[#0A0F1C] text-white focus:ring-sky-400"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border-white/15 bg-[#111827] text-white">
                      <SelectItem value="customer-support" data-testid="service-option-support">Customer Support</SelectItem>
                      <SelectItem value="back-office" data-testid="service-option-backoffice">Back-Office Operations</SelectItem>
                      <SelectItem value="data-entry" data-testid="service-option-data">Data Entry & Processing</SelectItem>
                      <SelectItem value="technical-support" data-testid="service-option-tech">Technical Support</SelectItem>
                      <SelectItem value="sales" data-testid="service-option-sales">Sales & Telemarketing</SelectItem>
                      <SelectItem value="other" data-testid="service-option-other">Something Else</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400">
                  What do you need? *
                </label>
                <Textarea
                  id="contact-message"
                  data-testid="contact-message-input"
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="e.g. We need 10 agents covering live chat and email, 7 days a week…"
                  className="resize-none rounded-none border-white/15 bg-[#0A0F1C] text-white placeholder:text-slate-600 focus-visible:ring-sky-400"
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit-button"
                disabled={loading}
                className="group h-14 w-full rounded-none bg-sky-400 font-display text-base font-bold tracking-wide text-[#0A0F1C] transition-colors duration-300 hover:bg-white disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send Enquiry
                    <ArrowUpRight className="ml-2 h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </Reveal>
      </div>

      <Reveal delay={0.1} className="mt-20">
        <div data-testid="map-embed" className="relative border border-white/10">
          <iframe
            title="Future Solutions — Kurnool office location map"
            src="https://www.google.com/maps?q=Kurnool,+Andhra+Pradesh,+India&output=embed"
            className="h-[400px] w-full"
            style={{ filter: "grayscale(1) invert(0.9) contrast(0.95)" }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=Kurnool,+Andhra+Pradesh,+India"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="map-directions-link"
            className="group absolute bottom-5 left-5 inline-flex items-center gap-2 rounded-full bg-sky-400 px-6 py-3 text-sm font-bold text-[#0A0F1C] transition-colors duration-300 hover:bg-white"
          >
            Get Directions
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>
      </Reveal>
    </section>
  );
};

export default Contact;
